import { Injectable } from '@angular/core';
import jwt_decode from 'jwt-decode';
import { BaseTokenInterface } from '../../shared/interfaces/base-token-service.interface';
import { BaseDateService } from '../../shared/services/base-date.service';
import { BaseStoreService } from './base-store.service';
import { AuthenticationSuccessfulResponse } from '../../../api';

export enum BaseTokenKeys {
  token = 'token',
  refreshToken = 'refresh-token',
  refreshTokenExp = 'refresh-token-exp',
}

@Injectable({
  providedIn: 'root',
})
export class BaseTokenService {
  constructor(private readonly storeService: BaseStoreService) {}

  setToken(token: string): void {
    this.storeService.set<string>(BaseTokenKeys.token, token);
  }

  setTokens(tokens: AuthenticationSuccessfulResponse): void {
    this.storeService.set<string>(BaseTokenKeys.token, tokens.token);
    this.storeService.set<string>(BaseTokenKeys.refreshToken, tokens.refresh_token);
    this.storeService.set<string>(BaseTokenKeys.refreshTokenExp, tokens.refresh_token_expiration);
  }

  getToken(): string | null {
    const value = this.storeService.get<string>(BaseTokenKeys.token);
    return typeof value === 'string' ? value : null;
  }

  getRefreshToken(): string | null {
    const value = this.storeService.get<string>(BaseTokenKeys.refreshToken);
    return typeof value === 'string' ? value : null;
  }

  decodeToken(token: string): BaseTokenInterface {
    return jwt_decode(token);
  }

  deleteTokens(): void {
    this.storeService.delete(BaseTokenKeys.token);
    this.storeService.delete(BaseTokenKeys.refreshToken);
    this.storeService.delete(BaseTokenKeys.refreshTokenExp);
  }

  isValid(token: string | null): boolean {
    if (!token) {
      return false;
    }

    const expireDate = this.decodeToken(token)?.exp;
    return expireDate ? BaseDateService.isBefore(new Date(), new Date(expireDate * 1000)) : false;
  }

  isRefreshTokenValid(): boolean {
    const expireDate = this.getRefreshToken();
    return expireDate ? BaseDateService.isBefore(new Date(), new Date(+expireDate * 1000)) : false;
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    const isValid = !!token && this.isValid(token);
    return isValid || this.isRefreshTokenValid();
  }
}
